import React, {FC, memo, useEffect, useState} from "react";
import {Button, Space} from "antd";
import {DownloadOutlined, PrinterOutlined, CloseOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import DraggableModal from "../../../../views/DraggableModal/DraggableModal";
import {generateLetterPdf, generateBatchLetterPdf, LetterPdfData} from "./generateLetterPdf";

const A4_ASPECT_RATIO = 210 / 297;
const PREVIEW_WIDTH = 800;
const PREVIEW_HEIGHT = PREVIEW_WIDTH / A4_ASPECT_RATIO;
const MODAL_WIDTH = PREVIEW_WIDTH + 50;

interface LetterPdfBatchPreviewModalProps {
    open: boolean;
    onClose: () => void;
    letterDataList: LetterPdfData[];
    fileNamePrefix?: string;
}

const LetterPdfBatchPreviewModal: FC<LetterPdfBatchPreviewModalProps> = ({
    open,
    onClose,
    letterDataList,
    fileNamePrefix,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [pdfDataUri, setPdfDataUri] = useState<string>("");

    // Index zuruecksetzen wenn Modal geoeffnet wird
    useEffect(() => {
        if (open) {
            setCurrentIndex(0);
        }
    }, [open]);

    // PDF-Vorschau generieren wenn Index oder Daten sich aendern
    useEffect(() => {
        if (open && letterDataList.length > 0 && currentIndex < letterDataList.length) {
            let cancelled = false;
            generateLetterPdf(letterDataList[currentIndex], "preview").then((dataUri) => {
                if (!cancelled && typeof dataUri === "string") {
                    setPdfDataUri(dataUri);
                }
            });
            return () => {
                cancelled = true;
            };
        } else {
            setPdfDataUri("");
        }
    }, [open, letterDataList, currentIndex]);

    const currentLetter = letterDataList[currentIndex];
    const recipientName = currentLetter
        ? [currentLetter.recipientFirstName, currentLetter.recipientLastName].filter(Boolean).join(" ") || "Empfaenger"
        : "Empfaenger";

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < letterDataList.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleExportAll = () => {
        if (letterDataList.length === 0) return;
        generateBatchLetterPdf(
            letterDataList,
            "export",
            fileNamePrefix ? fileNamePrefix + "_alle" : "Briefe_alle",
        );
    };

    const handleExportSingle = () => {
        if (!currentLetter) return;
        const singleFileName = fileNamePrefix
            ? fileNamePrefix + "_" + recipientName
            : "Brief_" + recipientName;
        generateLetterPdf(currentLetter, "export", singleFileName);
    };

    const handlePrint = () => {
        if (!currentLetter) return;
        generateLetterPdf(currentLetter, "print");
    };

    return (
        <DraggableModal
            draggable={true}
            boundsPadding={500}
            open={open}
            onCancel={onClose}
            destroyOnClose
            width={MODAL_WIDTH}
            style={{top: 30}}
            title={
                <div style={{display: "flex", alignItems: "center", gap: 12}}>
                    <span>{"Brief-Vorschau \u2014 " + recipientName}</span>
                    {letterDataList.length > 1 && (
                        <Space size="small">
                            <Button
                                size="small"
                                icon={<LeftOutlined />}
                                onClick={handlePrev}
                                disabled={currentIndex === 0}
                            />
                            <span style={{fontSize: 13, minWidth: 60, textAlign: "center", display: "inline-block"}}>
                                {(currentIndex + 1) + " von " + letterDataList.length}
                            </span>
                            <Button
                                size="small"
                                icon={<RightOutlined />}
                                onClick={handleNext}
                                disabled={currentIndex >= letterDataList.length - 1}
                            />
                        </Space>
                    )}
                </div>
            }
            footer={
                <div style={{display: "flex", justifyContent: "space-between"}}>
                    <div>
                        <Button
                            icon={<DownloadOutlined />}
                            type="primary"
                            onClick={handleExportAll}
                        >
                            {"Alle " + letterDataList.length + " Briefe herunterladen"}
                        </Button>
                    </div>
                    <Space>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportSingle}
                        >
                            {"Diesen Brief"}
                        </Button>
                        <Button
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            {"Drucken"}
                        </Button>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onClose}
                        >
                            {"Schließen"}
                        </Button>
                    </Space>
                </div>
            }
        >
            <div>
                {pdfDataUri ? (
                    <embed
                        width={PREVIEW_WIDTH}
                        height={PREVIEW_HEIGHT}
                        src={pdfDataUri + "#toolbar=0&view=FitH&scrollbar=0"}
                    />
                ) : null}
            </div>
        </DraggableModal>
    );
};

export default memo(LetterPdfBatchPreviewModal);
